import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./PlaylistDetails.css";
import { encodeId, TruncateName } from "../HelperMethods";
import { THEMES } from "../../types/ThemeTypes";
import {
    Playlist,
    PlaylistSong,
    EnrichedSong,
    PlaylistDetailsProps
} from "../../types/PlaylistTypes";
import TextCardComponent from "../TextCardComponent";
import { authFetch, getCurrentUserId } from "../../utils/authUtils";

const PlaylistDetails: React.FC<PlaylistDetailsProps> = ({
    playlistId,
    onEdit
}) => {
    const [playlist, setPlaylist] = useState<Playlist | null>(null);
    const [songs, setSongs] = useState<PlaylistSong[]>([]);
    const [enrichedSongs, setEnrichedSongs] = useState<EnrichedSong[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const navigate = useNavigate();
    const playlistRef = useRef<HTMLDivElement>(null);

    // Get the current user ID once at component initialization
    const currentUserId = getCurrentUserId();

    useEffect(() => {
        const handleScroll = (e: WheelEvent) => {
            if (playlistRef.current) {
                e.preventDefault();
                playlistRef.current.scrollTop += e.deltaY;
            }
        };

        // Check if the screen width is greater than mobile breakpoint (768px)
        const mediaQuery = window.matchMedia('(min-width: 768px)');

        const handleMediaQueryChange = (e: MediaQueryListEvent) => {
            if (e.matches) {
                window.addEventListener('wheel', handleScroll, { passive: false });
            } else {
                window.removeEventListener('wheel', handleScroll);
            }
        };

        // Initial check
        if (mediaQuery.matches) {
            window.addEventListener('wheel', handleScroll, { passive: false });
        }

        // Listen for changes in screen size
        mediaQuery.addEventListener('change', handleMediaQueryChange);

        return () => {
            window.removeEventListener('wheel', handleScroll);
            mediaQuery.removeEventListener('change', handleMediaQueryChange);
        };
    }, []);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this playlist? This action cannot be undone.')) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await authFetch(`/api/playlists/${playlistId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error(`Failed to delete playlist: ${response.status}`);
            }

            // Redirect to the profile page after successful deletion
            navigate('/profile');
        } catch (err) {
            console.error("Error deleting playlist:", err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        const fetchPlaylistDetails = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Fetch the playlist info
                const playlistResponse = await authFetch(`/api/playlists/${playlistId}`);

                if (!playlistResponse.ok) {
                    throw new Error(`Failed to fetch playlist: ${playlistResponse.status}`);
                }

                const playlistData = await playlistResponse.json();
                setPlaylist(playlistData);

                // Fetch the songs in the playlist
                const songsResponse = await authFetch(`/api/playlists/${playlistId}/songs`);

                if (!songsResponse.ok) {
                    throw new Error(`Failed to fetch songs: ${songsResponse.status}`);
                }

                const songsData = await songsResponse.json();
                setSongs(songsData);

                // Get detailed song info for each song
                const enrichedSongsPromises = songsData.map(async (song: PlaylistSong) => {
                    try {
                        const songResponse = await authFetch(`/api/songs/details/${song.melon_song_id}`);
                        if (!songResponse.ok) {
                            return song;
                        }
                        const songData = await songResponse.json();
                        return {
                            ...song,
                            ...songData,
                        };
                    } catch (err) {
                        return song;
                    }
                });

                const enrichedSongsData = await Promise.all(enrichedSongsPromises);
                setEnrichedSongs(enrichedSongsData);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setIsLoading(false);
            }
        };

        if (playlistId) {
            fetchPlaylistDetails();
        }
    }, [playlistId]);

    const handleSongClick = (songId: number) => {
        navigate(`/song/${encodeId(songId)}`);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    if (isLoading) {
        return <div className="playlist-details loading">Loading playlist...</div>;
    }

    if (error) {
        return <div className="playlist-details error">Error: {error}</div>;
    }

    if (!playlist) {
        return <div className="playlist-details not-found">Playlist not found</div>;
    }

    return (
        <div className="playlist-details" ref={playlistRef} style={{ backgroundColor: playlist ? THEMES[playlist.theme as keyof typeof THEMES].color : THEMES.SING.color }}>
            <div className="playlist-header">
                {/* <h5>Tags here</h5> */}
                {playlist && <h6>
                    <img src={playlist.picture}
                        alt="profile" className="profile-image-icon"
                        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                            event.currentTarget.src = "/dubuFace260.jpg";
                            event.currentTarget.onerror = null;
                        }}
                    />
                    {playlist.dubu_user}
                </h6>}
                <h6><strong>PLAYLIST</strong> <i>Created: {formatDate(playlist.created_at)}</i></h6>
                <div className="playlist-actions">
                    <span className={`visibility-badge ${playlist.is_public ? 'public' : 'private'}`}>
                        {playlist.is_public ? 'Public' : 'Private'}
                    </span>
                    {playlist.user_id === currentUserId && (
                        <>
                            {onEdit && (
                                <button className="edit-button" onClick={onEdit}>
                                    <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                                </button>
                            )}
                            <button
                                className="delete-button"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                <i className="fa fa-trash-o" aria-hidden="true"></i>
                            </button>
                        </>
                    )}
                </div>
            </div>
            <div className="playlist-desc">
                <div className="playlist-header-text">
                    <div className="main-text"><strong>{playlist.name}</strong></div>
                    {playlist.description && <h6><i>{playlist.description}</i></h6>}
                </div>
                <img className="dubu-music-image" src={playlist ? THEMES[playlist.theme as keyof typeof THEMES].image : THEMES.SING.image}
                    alt="dubu"
                />
            </div>
            <div className="playlist-container">

                <div className="playlist-container-left">

                    <div className="songs-container">
                        {enrichedSongs.length === 0 ? (
                            <p>No songs in this playlist yet.</p>
                        ) : (
                            <div className="songs-list">
                                {enrichedSongs.map((song, index) => {
                                    return (
                                        <div
                                            key={`${song.melon_song_id}-${index}`}
                                            className="song-item"
                                            onClick={() => handleSongClick(song.melon_song_id)}
                                        >
                                            <span className="song-number">{index + 1}</span>
                                            <div className="song-display-wrapper">
                                                <div
                                                    className="album-display-photo-wrapper"
                                                    style={{
                                                        backgroundImage: `url(${song.picture_url})`,
                                                    }}
                                                >
                                                </div>
                                                <div className="song-playlist-details">
                                                    {song.artist_ids && song.artist_ids.length == 1 && (
                                                        <h6>
                                                            <Link
                                                                key={song.artist_ids.toString()}
                                                                state={{ id: song.artist_ids }}
                                                                onClick={() => {
                                                                    window.location.href = `/artist/${encodeId(song.artist_ids[0])}`;
                                                                }}
                                                                to={""}
                                                            >
                                                                {TruncateName(song.artist_names[0], 30)}
                                                            </Link>
                                                        </h6>
                                                    )}
                                                    {song.artist_ids && song.artist_ids.length > 1 && (
                                                        <h6>
                                                            {TruncateName(song.artist_names.join(", "), 30)}
                                                        </h6>
                                                    )}
                                                    <Link
                                                        key={song.melon_song_id}
                                                        to={`/song/${encodeId(song.melon_song_id)}`}
                                                        state={{ song_id: song.melon_song_id, album_id: song.melon_album_id }}
                                                    >
                                                        {song.is_title && (
                                                            <p>
                                                                ✰ <strong>{song.english_name}</strong>
                                                            </p>
                                                        )}
                                                        {!song.is_title && (
                                                            <p>
                                                                <strong>{song.english_name}</strong>
                                                            </p>
                                                        )} </Link>
                                                </div>

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
                {/* <div className="playlist-container-right">
                    <TextCardComponent description={`Created: ${formatDate(playlist.created_at)} Updated: ${formatDate(playlist.updated_at)}`} />

                </div> */}
            </div>
            <br></br>
        </div>
    );
};

export default PlaylistDetails;
